import { createBrowserRouter } from "react-router";

import { ProtectedRoute } from "@/components/guards/protected-route";
import { PublicRoute } from "@/components/guards/public-route";
import { AppShell } from "@/components/layouts/app-shell";
import { RootLayout } from "@/components/layouts/root-layout";
import IntegrationsPage from "@/pages/account/integrations";
import AccountSettings from "@/pages/account/settings";
import UsageView from "@/pages/account/usage";
import AdminPage from "@/pages/admin";
import Login from "@/pages/auth/login";
import BillingSuccess from "@/pages/billing/success";
import Device from "@/pages/device";
import ErrorPage from "@/pages/error";
import Index from "@/pages/index";
import NotFound from "@/pages/not-found";
import PricingView from "@/pages/pricing";
import AskView from "@/pages/projects/ask";
import GraphView from "@/pages/projects/graph";
import JobsView from "@/pages/projects/jobs";
import SettingsView from "@/pages/projects/settings";
import WelcomeView from "@/pages/welcome";

export const router = createBrowserRouter([
	{
		element: <RootLayout />,
		errorElement: <ErrorPage />,
		children: [
			// Root: render Index, which makes its own auth+project decisions.
			// Don't gate it through PublicRoute — that path would create a
			// redirect loop for signed-in users (PublicRoute → / → PublicRoute).
			{ index: true, element: <Index /> },
			// Pricing is fully public — same as the marketing pages. Renders
			// the signed-in user's current plan as a highlight when there
			// is a session, but doesn't require one.
			{ path: "pricing", element: <PricingView /> },
			{
				path: "/",
				element: <PublicRoute />,
				children: [{ path: "login", element: <Login /> }],
			},
			{
				path: "/",
				element: <ProtectedRoute />,
				children: [
					{ path: "device", element: <Device /> },
					{
						path: "projects/:id",
						element: (
							<AppShell>
								<GraphView />
							</AppShell>
						),
					},
					{
						path: "projects/:id/graph",
						element: (
							<AppShell>
								<GraphView />
							</AppShell>
						),
					},
					{
						path: "projects/:id/jobs",
						element: (
							<AppShell>
								<JobsView />
							</AppShell>
						),
					},
					{
						path: "projects/:id/ask",
						element: (
							<AppShell>
								<AskView />
							</AppShell>
						),
					},
					{
						path: "projects/:id/settings",
						element: (
							<AppShell>
								<SettingsView />
							</AppShell>
						),
					},
					{
						path: "admin",
						element: (
							<AppShell>
								<AdminPage />
							</AppShell>
						),
					},
					{
						path: "account/usage",
						element: (
							<AppShell>
								<UsageView />
							</AppShell>
						),
					},
					{
						path: "account/settings",
						element: (
							<AppShell>
								<AccountSettings />
							</AppShell>
						),
					},
					{
						path: "account/integrations",
						element: (
							<AppShell>
								<IntegrationsPage />
							</AppShell>
						),
					},
					// /billing/success is signed-in only — Whop's checkout was
					// initiated from a session-authenticated CTA, so the user
					// is back with that session by the time they land here.
					{ path: "billing/success", element: <BillingSuccess /> },
					// /welcome is the onboarding agent surface (spec 27).
					// Full-bleed, no AppShell — the agent surface owns the
					// whole viewport. Signed-in only; if the user has already
					// completed or skipped, WelcomeView routes them out.
					{ path: "welcome", element: <WelcomeView /> },
				],
			},
			{ path: "*", element: <NotFound /> },
		],
	},
]);
